import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../lib/utils';
import { Coins, CheckCircle, AlertTriangle, Banknote, Trash2, RefreshCw, Mail } from 'lucide-react';

const BILLETS = [
  { key: '500', value: 500, color: '#9B59B6', textColor: '#fff' },
  { key: '200', value: 200, color: '#F1C40F', textColor: '#333' },
  { key: '100', value: 100, color: '#27AE60', textColor: '#fff' },
  { key: '50', value: 50, color: '#E67E22', textColor: '#fff' },
  { key: '20', value: 20, color: '#3498DB', textColor: '#fff' },
  { key: '10', value: 10, color: '#E74C3C', textColor: '#fff' },
  { key: '5', value: 5, color: '#95A5A6', textColor: '#fff' },
];
const PIECES = [
  { key: '2', value: 2, label: '2 EUR', color: '#C0C0C0' },
  { key: '1', value: 1, label: '1 EUR', color: '#FFD700' },
  { key: '0.5', value: 0.5, label: '50c', color: '#FFD700' },
  { key: '0.2', value: 0.2, label: '20c', color: '#FFD700' },
  { key: '0.1', value: 0.1, label: '10c', color: '#FFD700' },
  { key: '0.05', value: 0.05, label: '5c', color: '#CD7F32' },
  { key: '0.02', value: 0.02, label: '2c', color: '#CD7F32' },
  { key: '0.01', value: 0.01, label: '1c', color: '#CD7F32' },
];

const todayStr = () => new Date().toISOString().slice(0, 10);

export function VerifEnveloppePage() {
  const { api, user } = useAuth();
  const [date, setDate] = useState(todayStr());
  const [employe, setEmploye] = useState(user?.nom || '');
  const [billets, setBillets] = useState({});
  const [pieces, setPieces] = useState({});
  const [commentaire, setCommentaire] = useState('');
  const [caEspeces, setCaEspeces] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [saving, setSaving] = useState(false);

  const total = BILLETS.reduce((s, b) => s + b.value * (parseInt(billets[b.key]) || 0), 0)
    + PIECES.reduce((s, p) => s + p.value * (parseInt(pieces[p.key]) || 0), 0);
  const ecart = caEspeces !== null ? total - caEspeces : null;

  const fetchCa = useCallback(async () => {
    try {
      const r = await api.get('/verification-enveloppe/ca-especes', { params: { date } });
      setCaEspeces(r.data.ca_especes);
    } catch { setCaEspeces(null); }
  }, [api, date]);

  const fetchHistorique = useCallback(async () => {
    try {
      const r = await api.get('/verification-enveloppe');
      setHistorique(r.data || []);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { fetchCa(); }, [fetchCa]);
  useEffect(() => { fetchHistorique(); }, [fetchHistorique]);

  const reset = () => { setBillets({}); setPieces({}); setCommentaire(''); };

  const save = async () => {
    if (total <= 0) { toast.error('Saisis au moins un billet ou une piece'); return; }
    setSaving(true);
    try {
      await api.post('/verification-enveloppe', { date, employe, billets, pieces, commentaire });
      toast.success('Verification enregistree');
      reset();
      fetchHistorique();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/verification-enveloppe/${id}`);
      toast.success('Supprime');
      fetchHistorique();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suppression impossible');
    }
  };

  return (
    <div className="space-y-6" data-testid="verif-enveloppe-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Mail className="w-6 h-6 text-primary" /> Verif Enveloppe</h1>
          <p className="text-sm text-muted-foreground">Compte l'enveloppe du jour : saisis le nombre de billets et pieces, l'ecart se calcule tout seul.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Billets */}
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Banknote className="w-4 h-4" /> Billets</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {BILLETS.map(b => (
              <div key={b.key} className="flex items-center gap-3 p-2 rounded-lg border border-border">
                <div className="w-16 h-9 rounded-md flex items-center justify-center font-bold text-xs shadow" style={{ backgroundColor: b.color, color: b.textColor }}>{b.key} EUR</div>
                <Input type="number" min="0" inputMode="numeric" value={billets[b.key] || ''} placeholder="0" className="w-20 text-center font-mono"
                  onChange={(e) => setBillets(v => ({ ...v, [b.key]: e.target.value }))} data-testid={`billet-${b.key}`} />
                <div className="flex-1 text-right font-mono tabular-nums text-sm">{formatCurrency(b.value * (parseInt(billets[b.key]) || 0))}</div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Pieces */}
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Coins className="w-4 h-4" /> Pieces</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {PIECES.map(p => (
              <div key={p.key} className="flex items-center gap-3 p-1.5 rounded-lg border border-border">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-[10px] shadow" style={{ backgroundColor: p.color, color: '#333' }}>{p.label}</div>
                <Input type="number" min="0" inputMode="numeric" value={pieces[p.key] || ''} placeholder="0" className="w-20 text-center font-mono"
                  onChange={(e) => setPieces(v => ({ ...v, [p.key]: e.target.value }))} data-testid={`piece-${p.key}`} />
                <div className="flex-1 text-right font-mono tabular-nums text-sm">{formatCurrency(p.value * (parseInt(pieces[p.key]) || 0))}</div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recap + enregistrement */}
        <Card className="h-fit lg:sticky lg:top-4">
          <CardHeader className="py-3"><CardTitle className="text-sm">Recapitulatif</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="env-date" />
            </div>
            <div className="space-y-2">
              <Label>Compte par</Label>
              <Input value={employe} onChange={(e) => setEmploye(e.target.value)} placeholder="Ton prenom" data-testid="env-employe" />
            </div>
            <div className="rounded-xl border border-border p-4 space-y-2 bg-muted/30">
              <div className="flex justify-between text-sm"><span>Total compte</span><span className="font-bold tabular-nums text-lg">{formatCurrency(total)}</span></div>
              <div className="flex justify-between text-sm items-center">
                <span className="flex items-center gap-1">CA especes du jour
                  <button type="button" onClick={fetchCa} title="Actualiser" className="text-muted-foreground hover:text-foreground"><RefreshCw className="w-3 h-3" /></button>
                </span>
                <span className="font-medium tabular-nums">{caEspeces !== null ? formatCurrency(caEspeces) : '--'}</span>
              </div>
              {ecart !== null && (
                <div className="flex justify-between items-center pt-2 border-t border-border">
                  <span className="text-sm font-medium">Ecart</span>
                  <Badge className={`text-sm px-3 py-1 ${Math.abs(ecart) < 5 ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}`}>
                    {Math.abs(ecart) < 5 ? <CheckCircle className="w-4 h-4 mr-1" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
                    {ecart >= 0 ? '+' : ''}{formatCurrency(ecart)}
                  </Badge>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Commentaire (optionnel)</Label>
              <Input value={commentaire} onChange={(e) => setCommentaire(e.target.value)} placeholder="Remarque..." data-testid="env-commentaire" />
            </div>
            <div className="flex gap-2">
              <Button className="flex-1" onClick={save} disabled={saving || total <= 0} data-testid="env-save">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
              <Button variant="outline" onClick={reset}>Vider</Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Historique */}
      <Card>
        <CardHeader className="py-3"><CardTitle className="text-sm">Historique des verifications</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Compte par</TableHead>
                <TableHead className="text-right">Total compte</TableHead>
                <TableHead className="text-right">CA especes</TableHead>
                <TableHead className="text-right">Ecart</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Commentaire</TableHead>
                {user?.role === 'admin' && <TableHead className="w-10"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {historique.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">Aucune verification pour l'instant</TableCell></TableRow>
              )}
              {historique.map(h => (
                <TableRow key={h.id}>
                  <TableCell>{formatDate(h.date)}</TableCell>
                  <TableCell className="font-medium">{h.employe || h.created_by}</TableCell>
                  <TableCell className="text-right tabular-nums font-bold">{formatCurrency(h.total_compte)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(h.ca_especes)}</TableCell>
                  <TableCell className={`text-right tabular-nums font-medium ${Math.abs(h.ecart) < 5 ? 'text-green-600' : 'text-red-600'}`}>
                    {h.ecart >= 0 ? '+' : ''}{formatCurrency(h.ecart)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={h.statut === 'OK' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}>{h.statut}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{h.commentaire}</TableCell>
                  {user?.role === 'admin' && (
                    <TableCell>
                      <button className="text-destructive hover:text-destructive/70" onClick={() => remove(h.id)} title="Supprimer">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
